import React, {Children, cloneElement, isValidElement} from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import {utcYear, utcMonth, utcMonday, utcDay, timeHour} from 'd3-time'
import {scaleLinear, scaleTime} from 'd3-scale'
import isNumber from 'lodash/isNumber'
import isString from 'lodash/isString'
import {closestToIndex} from './utils'
import Loader from 'components/Loader'
import Tooltip from 'components/Tooltip'
import Title from 'components/Title'
import Legend from 'components/Legend'
import Icon from 'components/Icon'
import Chart from './Chart'
import Layer from './Layer'
import propsChanged from 'utils/propsChanged'
import numberFormats from 'utils/numberFormats'
import moment from 'moment'

function getCleanDate(date) {
  return moment([date.getFullYear(), date.getMonth(), date.getDate()])
}

const formats = {
  'year': {
    interval: utcYear,
    format: date => getCleanDate(date).format('YYY')
  },
  'month': {
    interval: utcMonth,
    format: date => getCleanDate(date).format('MMM')
  },
  'week': {
    interval: utcMonday,
    format: date => getCleanDate(date).format('MMM, DD')
  },
  'day': {
    interval: utcDay,
    format: date => getCleanDate(date).format('MMM, DD')
  },
  'hour': {
    interval: timeHour,
    format: date => moment(date).format('HH:mm')
  }
}

export default class LineChart extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    xAxisKey: PropTypes.string.isRequired,
    title: PropTypes.node,
    legend: PropTypes.node,
    data: PropTypes.array,
    selected: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    today: PropTypes.instanceOf(Date),
    onSelect: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    loading: PropTypes.bool,
    invertAreaColors: PropTypes.bool,
    tooltip: PropTypes.func,
    noPoints: PropTypes.bool,
    noGrid: PropTypes.bool,
    margin: PropTypes.object,
    format: PropTypes.string,
    onClose: PropTypes.func
  }

  static defaultProps = {
    margin: {top: 30, right: 20, bottom: 40, left: 60}
  }

  static contextTypes = {
    currency: PropTypes.string
  }

  constructor(props) {
    super(props)
    this._cachedMinMax = false
  }

  componentWillReceiveProps(nextProps) {
    if (propsChanged(this.props, nextProps, ['data', 'width', 'height', 'min', 'max', 'flushCache'])) {
      this._cachedMinMax = false
    }
  }

  render() {
    const {className, loading, onMouseEnter, onMouseLeave} = this.props

    return (
      <div
        className={classnames('line-chart', className, {'is-loading': loading})}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {this.renderControls()}
        {this.renderTitle()}
        {this.renderLegend()}
        {this.renderChart()}
        {this.renderLoader()}
      </div>
    )
  }

  renderChart() {
    const {height, margin} = this.props

    return (
      <div className='line-chart_body'>
        <Chart
          height={height}
          onMouseMove={this.onMouseMove}
          onMouseLeave={this.onMouseLeave}
        >
          <rect className='line-chart_y-axis-background' x='0' y='0' width={margin.left} height={height} />
          {this.renderBackground()}
          {this.renderYAxis()}
          {this.renderSelection()}
          {this.renderToday()}
          {this.renderLines()}
        </Chart>
        {this.renderTooltip()}
        {this.renderTodayTip()}
      </div>
    )
  }

  renderControls() {
    const {onClose} = this.props
    if (!onClose) return null

    return (
      <div className='line-chart_controls'>
        <div className='line-chart_controls-item' onClick={onClose}>
          <Icon name='cross' size='12px' />
        </div>

      </div>
    )
  }

  renderTitle() {
    const {title} = this.props

    if (title) {
      if (isValidElement(title)) {
        return title
      } else {
        return <Title>{title}</Title>
      }
    } else {
      return null
    }
  }

  renderLegend() {
    const {legend} = this.props

    if (legend) {
      return <Legend inline>{legend}</Legend>
    } else {
      return null
    }
  }

  renderBackground() {
    const {margin, noGrid} = this.props
    if (noGrid) return null
    const {start: startDate, end: endDate} = this.getDateRange()
    if (!startDate || !endDate) return null

    const {width: chartWidth, height: chartHeight} = this.getChartSize()
    const dateScale = this.getDateScale(startDate, endDate)
    const ticks = this.xTicks(startDate, endDate)

    const scale = scaleTime()
      .domain([startDate, endDate])
      .range([0, chartWidth])

    return (
      <Layer
        className='line-chart_background'
        left={margin.left}
        top={margin.top}
      >
        {
          ticks.map((tick, index) => {
            const isFirst = index === 0
            const isLast = index === ticks.length - 1

            return (
              <g className='line-chart_background-item' key={index}>
                <rect
                  x={scale(tick)}
                  y='0'
                  width={scale(isLast ? endDate : ticks[index + 1]) - scale(tick)}
                  height={chartHeight}
                  className='line-chart_background-rect'
                />
                {
                  !isFirst && !isLast
                    ? this.renderLabel(scale(tick), chartHeight, formats[dateScale].format(tick))
                    : null
                }
              </g>
            )
          })
        }
      </Layer>
    )
  }

  renderLabel(x, y, title) {
    return (
      <text x={x} y={y} dx='.5em' dy='2ex' className='line-chart_background-text'>
        {title}
      </text>
    )
  }

  renderYAxis() {
    const {margin, noGrid, format} = this.props
    if (noGrid) return null
    const {currency: contextCurrency} = this.context
    const numberFormat = numberFormats[format]
    const {width, height} = this.getChartSize()
    const scale = this.yScale()
    const ticks = scale.ticks(5)
    const tickFormat = format
      ? value => format === 'currency' ? numberFormat(value, contextCurrency) : numberFormat(value)
      : scale.tickFormat(5, 's')
    const tickClassname = classnames({
      'number-format': !!format
    })

    return (
      <Layer
        className='line-chart_y-axis'
        left={margin.left}
        top={margin.top}
      >
        {
          ticks.map(tick => {
            const y = height - scale(tick)
            return (
              <g className='line-chart_y-axis-tick' key={tick}>
                <line x1={0} y1={y} x2={width} y2={y} />
                <text className={tickClassname} dx='-.5em' dy='.5ex' y={y}>{tickFormat(tick)}</text>
              </g>
            )
          })
        }
      </Layer>
    )
  }

  renderLines() {
    const {data, xAxisKey, selected, margin, noPoints, invertAreaColors} = this.props
    if (!data) return null
    const {height} = this.getChartSize()
    const xScale = this.xScale()
    const yScale = this.yScale()
    const lines = this.getLines()
    const xSelected = selected ? this.getIndexByX(xScale(moment(selected).toDate())) : null

    const values = lines.reduce((acc, line) => {
      acc[line.props.dataKey] = data.reduce((arr, item) => {
        if (item[line.props.dataKey] !== undefined) {
          arr.push({
            x: xScale(moment(item[xAxisKey]).toDate()),
            y: height - yScale(item[line.props.dataKey])
          })
        }
        return arr
      }, [])

      return acc
    }, {})

    return (
      <Layer
        className='line-chart_lines'
        left={margin.left}
        top={margin.top}
      >
        {lines.map((line, index) => cloneElement(line, {
          data: values[line.props.dataKey],
          areaData: isString(line.props.area) ? values[line.props.area] : null,
          invertAreaColors,
          selectedValueIndex: xSelected,
          noPoints: line.props.noPoints || noPoints,
          height,
          key: index
        }))}
      </Layer>
    )
  }

  renderSelection() {
    const {selected, data, margin} = this.props
    if (!selected || !data) return null
    const {height} = this.getChartSize()
    const x = this.xScale()(moment(selected).toDate())

    return (
      <Layer
        className='line-chart_selection'
        left={margin.left}
        top={margin.top}
      >
        <line x1={x} y1={0} x2={x} y2={height} />
      </Layer>
    )
  }

  renderToday() {
    const {today, data, margin} = this.props
    if (!today || !data) return null
    const {height} = this.getChartSize()
    const x = this.xScale()(moment(today).toDate())

    return (
      <Layer
        className='line-chart_today'
        left={margin.left}
        top={margin.top}
      >
        <line x1={x} y1={0} x2={x} y2={height} />
      </Layer>
    )
  }

  renderTooltip() {
    const {selected: rawSelected, data, tooltip, margin} = this.props
    if (!data || !tooltip) return null
    const {width, height} = this.getChartSize()
    const {start: startDate, end: endDate} = this.getDateRange()
    if (!rawSelected) return null
    const selected = moment(rawSelected).toDate()

    if (!selected || selected < startDate || selected > endDate) return null

    const x = this.xScale()(selected)

    const tooltipData = this.getValueByX(x)
    if (!tooltipData) return null

    const selectedLine = this.getLines().find(line => line.props.selected === true)

    let selectedLineKey = false
    if (selectedLine) {
      selectedLineKey = selectedLine.props.dataKey
    }
    const value = tooltipData[selectedLineKey]
    const y = selectedLineKey !== false && isNumber(value) ? height - this.yScale()(value) : 0

    return (
      <div className='line-chart_tooltip'>
        <Tooltip
          x={x + margin.left}
          y={y + margin.top}
          bounds={{x: margin.top, y: margin.left, width: width, height: height + 20}}
          place='auto'
        >
          {tooltip(tooltipData, selectedLineKey)}
        </Tooltip>
      </div>
    )
  }

  renderTodayTip() {
    const {today, data} = this.props
    if (!today || !data) return null
    if (data.length === 1) return null
    const x = this.xScale()(moment(today).toDate())

    return (
      <div className='line-chart_today-tip' style={{left: x}}>
        <span className='line-chart_today-tip-text'>Today</span>
      </div>
    )
  }

  renderLoader() {
    const {loading} = this.props

    if (loading) {
      return <Loader />
    } else {
      return null
    }
  }

  onMouseMove = ({mouseX, mouseY}) => {
    const {data, onSelect, xAxisKey, margin} = this.props
    if (!data) return
    const {height: chartHeight} = this.getChartSize()
    const x = mouseX - margin.left
    const y = mouseY - margin.top
    const dataItem = this.getValueByX(x)

    if (dataItem) {
      const keyNames = this.getLines().map(line => line.props.dataKey)
      const values = keyNames.map(key => dataItem[key])
      const yItemKey = closestToIndex(this.yScale().invert(chartHeight - y), values)

      onSelect && onSelect(dataItem[xAxisKey], keyNames[yItemKey])
    }
  }

  onMouseLeave = e => {
    const {onSelect} = this.props

    onSelect && onSelect(null, null)
  }

  getDateScale(startDate, endDate) {
    const start = moment(startDate)
    const end = moment(endDate)

    if (end.diff(start, 'years') > 1) {
      return 'year'
    } else if (end.diff(start, 'months') > 1) {
      return 'month'
    } else if (end.isoWeek() - start.isoWeek() > 1) {
      return 'week'
    } else if (end.diff(start, 'days') > 1) {
      return 'day'
    } else {
      return 'hour'
    }
  }

  xTicks(startDate, endDate) {
    const dateScale = this.getDateScale(startDate, endDate)
    const scale = this.xScale()
    const start = moment(startDate).toDate()
    const end = moment(endDate).toDate()

    return [start, ...scale.ticks(formats[dateScale].interval), end]
  }

  getMinMax() {
    const {data, min, max} = this.props
    const lines = this.getLines().map(line => line.props.dataKey)

    if (!this._cachedMinMax) {
      this._cachedMinMax = this.minMax(data, lines, min, max)
    }

    return this._cachedMinMax
  }

  minMax(data, lines, min, max) {
    if (!data) return {min: 0, max: 0}
    if (!min || !max) {
      let realMin = 0
      let realMax = 0
      data.forEach(item => {
        lines.forEach(key => {
          if (item[key]) {
            realMin = realMin === undefined ? item[key] : Math.min(item[key], realMin)
            realMax = realMax === undefined ? item[key] : Math.max(item[key], realMax)
          }
        })
      })

      const threshold = (realMax - realMin) * 0.1
      return {
        min: min === undefined ? realMin - threshold : min,
        max: max === undefined ? realMax + threshold : max
      }
    } else {
      return {min, max}
    }
  }

  xScale() {
    const {start: startDate, end: endDate} = this.getDateRange()
    const {width: chartWidth} = this.getChartSize()

    return scaleTime()
      .domain([startDate, endDate])
      .range([0, chartWidth])
  }

  yScale() {
    const {height} = this.getChartSize()
    const {min, max} = this.getMinMax()

    return scaleLinear()
      .domain([min, max])
      .range([0, height])
  }

  getLines() {
    return Children.toArray(this.props.children)
  }

  getChartSize() {
    const {width, height, margin} = this.props

    return {
      width: width - margin.left - margin.right,
      height: height - margin.top - margin.bottom
    }
  }

  getDateRange() {
    const {data, xAxisKey} = this.props

    if (data && data.length) {
      return {
        start: moment(data[0][xAxisKey]).toDate(),
        end: moment(data[data.length - 1][xAxisKey]).toDate()
      }
    } else {
      return {}
    }
  }

  getIndexByX(x) {
    const {data, xAxisKey} = this.props
    const scale = this.xScale()
    const dateX = scale.invert(x)

    return closestToIndex(dateX, data.map(item => item[xAxisKey]))
  }

  getValueByX(x) {
    const {data} = this.props
    const index = this.getIndexByX(x)

    return index !== null ? data[index] : null
  }
}



// WEBPACK FOOTER //
// ./client/components/Chart/LineChart.js